import { NetworkImage, PlaceholderImage, Typography } from "@/components/atoms";
import { Logo } from "@/components/atoms/Logo";
import { ImageCarousel } from "@/components/molecules/ImageCarousel";
import { LayoutToggle, type LayoutType } from "@/components/molecules";
import { DT_SCREEN } from "@/core/constants/dynatraceScreens";
import { useCatalogDynatraceView } from "@/core/hooks/useCatalogDynatraceView";
import { useTranslation, useUIColors } from "@/core/hooks";
import {
  trackCatalogEvent,
  trackGuestLanguageChange,
  trackGuestLoginOverlayClick,
  trackGuestProjectCarouselManualChange,
} from "@/core/services/trackDynatraceCatalog";
import { useI18nContext } from "@/core/providers/I18nProvider";
import { resolveGreetingPrefix } from "@/core/utils/greeting";
import { GuestListEmptyState } from "@/modules/guest-web/components/GuestListEmptyState";
import { useGuestLabels } from "@/modules/guest-web/hooks/useGuestLabels";
import { useGuestProjectImages } from "@/modules/guest-web/hooks/useGuestProjectImages";
import { useGuestPropertiesList } from "@/modules/guest-web/hooks/useGuestPropertiesList";
import type { GuestPropertyCardModel } from "@/modules/guest-web/types/guest.types";
import { PropertyListingCardSkeleton } from "@/modules/home/components/PropertyListingCardSkeleton";
import { GUEST_LANGUAGE_TOGGLE_WIDTH, GuestLanguageToggle } from "./GuestLanguageToggle";
import { GuestFloatingLoginButtonFixed } from "./GuestFloatingLoginButtonFixed";
import { GuestLoginPromptDialogMobile } from "./GuestLoginPromptDialogMobile";
import { router } from "expo-router";
import React, { useCallback, useMemo, useState } from "react";
import { ImageSourcePropType, Pressable, ScrollView, StyleSheet, View } from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";

const SKELETON_KEYS = ["skeleton-1", "skeleton-2", "skeleton-3"] as const;
const COMPACT_THUMB_SIZE = 96;
const GRID_CAROUSEL_HEIGHT = 200;
const MAX_VISIBLE_UNIT_TYPE_TAGS = 4;

function sortMobileGuestPropertiesByContentfulOrder(
  properties: ReadonlyArray<GuestPropertyCardModel>,
  orderByProjectId: Readonly<Record<string, number>>,
): GuestPropertyCardModel[] {
  try {
    return properties
      .map((property, index) => ({ property, index }))
      .sort((left, right) => {
        const orderA = orderByProjectId[left.property.id];
        const orderB = orderByProjectId[right.property.id];
        const hasA = typeof orderA === "number" && Number.isFinite(orderA);
        const hasB = typeof orderB === "number" && Number.isFinite(orderB);
        if (hasA && hasB && orderA !== orderB) {
          return orderA - orderB;
        }
        if (hasA !== hasB) {
          return hasA ? -1 : 1;
        }
        return left.index - right.index;
      })
      .map(({ property }) => property);
  } catch {
    return [...properties];
  }
}

/**
 * Mobile-only unit-type tags row — the shared GuestUnitTypeTagsRow (guest-web,
 * used by web too) hides whichever tags don't fit the measured row width with
 * no indication anything was cut; here we deliberately cap at a fixed count
 * and show "+N" for the rest instead.
 */
function GuestUnitTypeTagsRowCapped({
  tags,
  tagBackgroundColor,
}: Readonly<{
  tags: GuestPropertyCardModel["typeTags"];
  tagBackgroundColor: string;
}>) {
  if (tags.length === 0) {
    return null;
  }
  const visibleTags = tags.slice(0, MAX_VISIBLE_UNIT_TYPE_TAGS);
  const overflowCount = tags.length - visibleTags.length;

  return (
    <View style={styles.tagsRow}>
      {visibleTags.map((tag) => (
        <View key={tag.id} style={[styles.tagPill, { backgroundColor: tagBackgroundColor }]}>
          <Typography variant="caption" color="primary" numberOfLines={1} style={styles.tagPillText}>
            {tag.label}
          </Typography>
        </View>
      ))}
      {overflowCount > 0 ? (
        <View style={[styles.tagPill, { backgroundColor: tagBackgroundColor }]}>
          <Typography variant="caption" color="primary" style={styles.tagPillText}>
            {`+${overflowCount}`}
          </Typography>
        </View>
      ) : null}
    </View>
  );
}

/**
 * Grid card — mobile-owned (not the shared, web-reused GuestPropertyCard)
 * specifically so the carousel dots can be forced white here without
 * touching that shared component's default (used by web too).
 */
function GuestPropertyCardMobile({
  property,
  onPress,
}: Readonly<{ property: GuestPropertyCardModel; onPress: () => void }>) {
  const colors = useUIColors();
  const { isRTL } = useI18nContext();
  const carouselImages: ImageSourcePropType[] = property.images.map((img) => ({ uri: img.uri }));
  const unitsBadgeSide = isRTL ? "right" : "left";
  const handleManualCarouselChange = useCallback(
    (imageIndex: number) => {
      trackGuestProjectCarouselManualChange(property.id, imageIndex);
    },
    [property.id],
  );

  return (
    <Pressable
      onPress={onPress}
      style={[styles.gridCard, { backgroundColor: colors.backgroundLevel1 }]}
    >
      <View style={styles.gridImageWrap}>
        {carouselImages.length > 0 ? (
          <ImageCarousel
            images={carouselImages}
            height={GRID_CAROUSEL_HEIGHT}
            activeDotColor={colors.white}
            onPress={onPress}
            onManualIndexChange={handleManualCarouselChange}
          />
        ) : (
          <View style={[styles.gridImagePlaceholder, { backgroundColor: colors.backgroundLevel2 }]}>
            <PlaceholderImage />
          </View>
        )}
        {property.unitsBadge ? (
          <View
            style={[
              styles.unitsBadge,
              { backgroundColor: colors.unitsBadgeBg, [unitsBadgeSide]: 12 },
            ]}
            pointerEvents="none"
          >
            <Typography variant="caption" color="primary" style={styles.unitsBadgeText}>
              {property.unitsBadge}
            </Typography>
          </View>
        ) : null}
      </View>
      <View style={styles.gridContent}>
        <Typography variant="headline" color="primary" numberOfLines={1}>
          {property.title}
        </Typography>
        {property.location ? (
          <Typography variant="caption" color="secondary" numberOfLines={2}>
            {property.location}
          </Typography>
        ) : null}
        <GuestUnitTypeTagsRowCapped
          tags={property.typeTags}
          tagBackgroundColor={colors.propertyTagBg}
        />
      </View>
    </Pressable>
  );
}

/** Compact "list" card — thumbnail + title/location/tags, mirrors PropertyListingCard's list layout. */
function GuestPropertyCardCompact({
  property,
  onPress,
}: Readonly<{ property: GuestPropertyCardModel; onPress: () => void }>) {
  const colors = useUIColors();
  const firstImageUri = property.images[0]?.uri;

  return (
    <View
      style={[
        styles.compactCard,
        { backgroundColor: colors.backgroundLevel1, borderColor: colors.backgroundLevel2 },
      ]}
    >
      <Pressable style={styles.compactRow} onPress={onPress}>
        <View style={styles.compactThumbWrap}>
          {firstImageUri ? (
            <NetworkImage url={firstImageUri} style={styles.compactThumb} resizeMode="cover" />
          ) : (
            <View style={[styles.compactThumb, { backgroundColor: colors.backgroundLevel2 }]}>
              <PlaceholderImage />
            </View>
          )}
        </View>
        <View style={styles.compactContent}>
          <Typography variant="headline" color="primary" numberOfLines={1}>
            {property.title}
          </Typography>
          {property.location ? (
            <Typography variant="caption" color="secondary" numberOfLines={1}>
              {property.location}
            </Typography>
          ) : null}
        </View>
      </Pressable>
      <GuestUnitTypeTagsRowCapped
        tags={property.typeTags}
        tagBackgroundColor={colors.propertyTagBg}
      />
    </View>
  );
}

/**
 * Mobile guest home — public project listing, no login required.
 * UI mirrors the after-login home screen (logo/language header, greeting +
 * grid/list toggle, listing cards) but reuses the already-built, fully
 * cross-platform guest-web presentational components (card, tags, empty
 * state, floating login button) and data hooks instead of duplicating them —
 * only the mobile-specific header/shell here is new.
 */
export function GuestHomeScreenMobile() {
  const colors = useUIColors();
  const { t, currentLanguage, changeLanguage } = useTranslation();
  const labels = useGuestLabels();
  const insets = useSafeAreaInsets();
  const [layout, setLayout] = useState<LayoutType>("grid");
  const [loginVisible, setLoginVisible] = useState(false);

  const { loadState, properties } = useGuestPropertiesList();
  const projectIds = useMemo(() => properties.map((property) => property.id), [properties]);
  const { orderByProjectId } = useGuestProjectImages(projectIds);
  const listedProperties = useMemo(
    () => sortMobileGuestPropertiesByContentfulOrder(properties, orderByProjectId),
    [properties, orderByProjectId],
  );
  const greeting = labels.greeting(resolveGreetingPrefix(t));

  useCatalogDynatraceView(
    DT_SCREEN.GUEST_HOME,
    "guest_home_viewed",
    loadState === "ready",
  );
  useCatalogDynatraceView(
    DT_SCREEN.GUEST,
    "guest_project_list_viewed",
    loadState === "ready",
  );

  const openDetails = (id: string) => {
    trackCatalogEvent({
      screen: DT_SCREEN.GUEST,
      actionName: "guest_project_card_click",
      eventType: "action",
      properties: { projectId: id },
    });
    router.push(`/guest-home-details?id=${encodeURIComponent(id)}`);
  };

  const goLogin = () => {
    setLoginVisible(false);
    // Clear the guest screens (and the original login screen underneath,
    // reached via "Continue as Guest") off the stack first, same pattern
    // app/login.tsx itself uses on a completed session — so device-back from
    // this fresh login screen lands on Welcome instead of back into guest
    // browsing.
    if (router.canGoBack()) {
      router.dismissAll();
    }
    router.replace("/login");
  };

  return (
    <View style={[styles.container, { backgroundColor: colors.backgroundLevel2 }]}>
      <View style={[styles.header, { paddingTop: insets.top + 8 }]}>
        {/* Same-width invisible spacer on the other side keeps the logo
            centered relative to the header, not just the leftover space
            next to the toggle. */}
        <View style={styles.headerSideSlot} pointerEvents="none" />
        <View style={styles.headerLogoSlot} pointerEvents="none">
          <Logo color="bloomRed" size="large" />
        </View>
        <GuestLanguageToggle
          value={currentLanguage}
          onChange={(value) => {
            if (value === currentLanguage) {
              return;
            }
            trackGuestLanguageChange(value);
            void changeLanguage(value);
          }}
        />
      </View>

      <ScrollView
        contentContainerStyle={[styles.content, { paddingBottom: 100 + insets.bottom }]}
        showsVerticalScrollIndicator={false}
      >
        <View style={styles.greetingRow}>
          <Typography variant="title2" color="primary">
            {greeting}
          </Typography>
          <LayoutToggle layout={layout} onLayoutChange={setLayout} />
        </View>

        {loadState === "loading" && (
          <View style={styles.list}>
            {SKELETON_KEYS.map((key) => (
              <PropertyListingCardSkeleton key={key} layout={layout} />
            ))}
          </View>
        )}

        {loadState === "empty" && <GuestListEmptyState title={labels.emptyNoProperties} />}
        {loadState === "error" && <GuestListEmptyState title={labels.emptyFetchError} />}

        {loadState === "ready" && (
          <View style={styles.list}>
            {listedProperties.map((property) =>
              layout === "grid" ? (
                <GuestPropertyCardMobile
                  key={property.id}
                  property={property}
                  onPress={() => openDetails(property.id)}
                />
              ) : (
                <GuestPropertyCardCompact
                  key={property.id}
                  property={property}
                  onPress={() => openDetails(property.id)}
                />
              ),
            )}
          </View>
        )}
      </ScrollView>

      <GuestFloatingLoginButtonFixed
        onPress={() => {
          trackGuestLoginOverlayClick();
          setLoginVisible(true);
        }}
      />
      <GuestLoginPromptDialogMobile
        visible={loginVisible}
        onMaybeLater={() => setLoginVisible(false)}
        onLogin={goLogin}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 16,
    paddingBottom: 8,
  },
  headerSideSlot: {
    width: GUEST_LANGUAGE_TOGGLE_WIDTH,
  },
  headerLogoSlot: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
  },
  content: {
    padding: 16,
    gap: 16,
  },
  greetingRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  list: {
    gap: 16,
  },
  compactCard: {
    borderRadius: 12,
    borderWidth: 1,
    padding: 12,
    gap: 12,
  },
  compactRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  compactThumbWrap: {
    width: COMPACT_THUMB_SIZE,
    height: COMPACT_THUMB_SIZE * 0.7,
    borderRadius: 8,
    overflow: "hidden",
  },
  compactThumb: {
    width: "100%",
    height: "100%",
  },
  compactContent: {
    flex: 1,
    minWidth: 0,
    gap: 4,
  },
  gridCard: {
    borderRadius: 12,
    padding: 8,
    gap: 8,
  },
  gridImageWrap: {
    height: GRID_CAROUSEL_HEIGHT,
    borderRadius: 8,
    overflow: "hidden",
    position: "relative",
  },
  gridImagePlaceholder: {
    width: "100%",
    height: "100%",
    alignItems: "center",
    justifyContent: "center",
  },
  unitsBadge: {
    position: "absolute",
    top: 12,
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 8,
    zIndex: 2,
    maxWidth: "80%",
  },
  unitsBadgeText: {
    fontFamily: "Matter-SemiBold",
  },
  gridContent: {
    gap: 8,
    marginTop: 4,
  },
  tagsRow: {
    flexDirection: "row",
    flexWrap: "nowrap",
    alignItems: "center",
    gap: 8,
  },
  tagPill: {
    paddingVertical: 6,
    paddingHorizontal: 12,
    borderRadius: 4,
    flexShrink: 0,
    alignItems: "center",
    justifyContent: "center",
  },
  // Font/Family, Font/Size/3XS Regular, weight 400, LH 100%, letter-spacing 0
  // (Typography's "caption" variant already gives Matter-Regular/12px/LH12).
  tagPillText: {
    fontWeight: "400",
    letterSpacing: 0,
    textAlign: "center",
  },
});

export default GuestHomeScreenMobile;
